/*
 * Copyright (c) 2025. Slimme Infrasoftware B.V. Apeldoorn /
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden
 * volgens de voorwaarden die zijn vastgelegd in de overeenkomst
 * tussen ATR en Slimme Infrasoftware B.V. en de onderliggende
 * overeenkomsten tussen Slimme Infrasoftware B.V en
 * Spin in het Web B.V.
 */

/*
 * filtertool-structuur.ts
 * Gegenereerd met Junie (AI), 29-09-2025
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Boomstructuur voor filtertool: root is altijd een EN-groep met clausules; binnen groepen kan EN of OF gebruikt worden.
 */

import {Filtertoolregel} from './filtertool-regels';
import {objectNaarFiltertoolregel} from './filtertool-regel-factory';

/**
 * Een clausule binnen de filterboom. In een groep worden kinderen gecombineerd volgens de operator van die groep.
 */
export type Filterclausule = FilterRegelClausule | FilterGroep;

/**
 * Clausule die één concrete filterregel bevat.
 */
export interface FilterRegelClausule {
  /** Discriminator voor het unietype. */
  soort: 'regel';
  /** Unieke identificatie voor mutaties en UI. */
  id: string;
  /** De daadwerkelijke filterregel. */
  regel: Filtertoolregel;
  /** Id van de parentgroep; bij root is dit null. */
  parentID: string | null;
}

/**
 * Groep die een operator heeft ('en' of 'of') en kinderen bevat.
 */
export interface FilterGroep {
  /** Discriminator voor het unietype. */
  soort: 'groep';
  /** Unieke identificatie voor mutaties en UI. */
  id: string;
  /** Operator die bepaalt hoe kinderen gecombineerd worden. */
  operator: 'en' | 'of';
  /** Kindclausules die onderling volgens operator worden gecombineerd. */
  kinderen: Filterclausule[];
  /** Optionele naam/label voor weergave. */
  naam?: string;
  /** Id van de parentgroep; bij root is dit null. */
  parentID: string | null;
}

/**
 * De volledige filterboom. Root is altijd een EN-groep. Een boom bevat dus nooit direct regels.
 */
export type Filterboom = FilterGroep;

///////////////////// Typeguards /////////////////////

/**
 * Controleert of een waarde een FilterRegelClausule is.
 */
export function isFilterRegelClausule(v: any): v is FilterRegelClausule {
  return !!v && v.soort === 'regel' && typeof v.id === 'string' && !!v.regel;
}

/**
 * Controleert of een waarde een FilterGroep is.
 */
export function isFilterGroep(v: any): v is FilterGroep {
  return !!v && v.soort === 'groep' && typeof v.id === 'string' && (v.operator === 'en' || v.operator === 'of') && Array.isArray(v.kinderen);
}



///////////////////// Parser /////////////////////

/**
 * Maakt een lege EN-rootgroep.
 */
export function maakLegeEnRoot(): Filterboom {
  return { soort: 'groep', id: 'root', operator: 'en', kinderen: [], parentID: null } as FilterGroep;
}

/**
 * Bepaalt of de volledige filterboom geldig is. Doorloopt alle clausules en valideert regels.
 */
export function isFilterboomGeldig(boom: Filterboom): boolean {
  const check = (node: Filterclausule): boolean => {
    if (!node) { return true; }
    switch (node.soort) {
      case 'regel':
        return !!((node as FilterRegelClausule).regel as any)?.valid;
      case 'groep':
        return (node as FilterGroep).kinderen.every(k => check(k));
      default:
        return true;
    }
  };
  return check(boom);
}

/**
 * Bepaalt of een specifieke groep (subtree) geldig is. Doorloopt recursief alle kinderen.
 */
export function isFilterGroepGeldig(groep: FilterGroep): boolean {
  if (!groep) { return true; }
  const check = (node: Filterclausule): boolean => {
    if (!node) { return true; }
    switch (node.soort) {
      case 'regel':
        return !!((node as FilterRegelClausule).regel as any)?.valid;
      case 'groep':
        return (node as FilterGroep).kinderen.every(k => check(k));
      default:
        return true;
    }
  };
  return check(groep);
}

/**
 * Serialiseer de boom naar platte JS-objecten (regels worden via toObject() geschreven).
 */
export function filterboomNaarObject(boom: Filterboom): any {
  const map = (node: Filterclausule): any => {
    if (!node) { return null; }
    switch (node.soort) {
      case 'regel':
        // parentID is alleen runtime-boekhouding en wordt niet weggeschreven
        return { soort: 'regel', id: node.id, regel: (node.regel as any)?.toObject?.() ?? null };
      case 'groep':
        // parentID is alleen runtime-boekhouding en wordt niet weggeschreven
        return {
          soort: 'groep',
          id: node.id,
          naam: (node as FilterGroep).naam,
          operator: (node as FilterGroep).operator,
          kinderen: (node as FilterGroep).kinderen.map(k => map(k)).filter(v => !!v)
        };
    }
  };
  return map(boom);
}

/**
 * Lees een boom in vanuit platte objecten. Regels worden via FiltertoolRegel.fromObject(...) gemaakt.
 */
export function filterboomVanObject(input: any): Filterboom {
  const parseClausule = (c: any, parentId: string | null): Filterclausule | null => {
    if (!c || typeof c !== 'object') return null;
    switch (c.soort) {
      case 'regel': {
        const regel = objectNaarFiltertoolregel(c.regel);
        if (!regel) return null;
        return { soort: 'regel', id: String(c.id || crypto.randomUUID()), regel, parentID: parentId };
      }
      case 'groep': {
        const operator = c.operator === 'of' ? 'of' : 'en';
        const id = String(c.id || crypto.randomUUID());
        const kinderenRauw = Array.isArray(c.kinderen) ? c.kinderen : [];
        const kinderen: Filterclausule[] = [];
        for (const k of kinderenRauw) {
          const parsed = parseClausule(k, id);
          if (parsed) kinderen.push(parsed);
        }
        return { soort: 'groep', id, naam: c.naam, operator, kinderen, parentID: parentId } as FilterGroep;
      }
      default:
        return null;
    }
  };

  // Geen input: lege EN-root.
  if (input == null) return maakLegeEnRoot();

  const root = parseClausule(input, null);
  if (root && root.soort === 'groep') {
    // Zorg dat de root EN is en parentID null
    if ((root as FilterGroep).operator === 'en') {
      return {...(root as FilterGroep), id: (root as FilterGroep).id || 'root', parentID: null} as FilterGroep;
    } else {
      // Maak een nieuwe EN-root met de bestaande groep als kind; zet parentID's correct
      const kind = root as FilterGroep;
      const enRoot: FilterGroep = { soort: 'groep', id: 'root', operator: 'en', kinderen: [ kind ], parentID: null };
      // herstel parentID van kind
      (kind as FilterGroep).parentID = enRoot.id;
      return enRoot;
    }
  }
  return maakLegeEnRoot();
}

/**
 * Probeert onbekende input te parsen naar een geldige filterboom. Backwards compatibele helper.
 */
export function parseFilterboom(input: any): Filterboom {
  return filterboomVanObject(input);
}
